import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Copy, Minimize2, LayoutPanelLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import './CommandsDropup.css'; // Import the CSS file

const CommandsDropup = ({ isOpen, onClose, onInsertCommand, buttonRef }) => {
  const dropupRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [portalContainer, setPortalContainer] = useState(null);

  // Commands configuration
  const commands = [
    {
      command: '/variations',
      description: 'Generate variations of a prompt',
      icon: <Copy className="h-4 w-4 text-blue-500" />,
    },
    {
      command: '/shorten',
      description: 'Create a shorter version of a prompt',
      icon: <Minimize2 className="h-4 w-4 text-green-500" />,
    },
    {
      command: '/extend',
      description: 'Add more details to a prompt',
      icon: <LayoutPanelLeft className="h-4 w-4 text-purple-500" />,
    }
  ];

  // Create portal container on mount
  useEffect(() => {
    // Check if the container already exists
    let div = document.getElementById('commands-dropup-portal');
    
    // Create it if it doesn't exist
    if (!div) {
      div = document.createElement('div');
      div.setAttribute('id', 'commands-dropup-portal');
      div.style.position = 'fixed'; // Use fixed instead of absolute
      div.style.top = '0';
      div.style.left = '0';
      div.style.width = '100%';
      div.style.height = '100%';
      div.style.zIndex = '9999';
      div.style.pointerEvents = 'none'; // Allow clicks to pass through by default
      document.body.appendChild(div);
    }
    
    setPortalContainer(div);

    return () => {
      // Only remove if we created it and it still exists
      if (document.getElementById('commands-dropup-portal')) {
        document.body.removeChild(div);
      }
    };
  }, []);

  // Helper function to get viewport-relative position
  const getViewportPosition = (element) => {
    const rect = element.getBoundingClientRect();
    return {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      bottom: rect.bottom,
      right: rect.right
    };
  };

  // Calculate position when button ref changes or when opened
  useEffect(() => {
    if (!isOpen) return;

    // Add a small delay to ensure the DOM is ready
    const timer = setTimeout(() => {
      // Add debug logging
      console.log('Calculating dropup position');
      
      // Try to get the button by ID first, then fall back to ref
      const buttonEl = document.getElementById('command-button') || 
                      document.getElementById('command-button-mobile') || 
                      (buttonRef?.current ? buttonRef.current : null);
      
      if (!buttonEl) {
        console.error('Could not find command button element');
        return;
      }
      
      // Get the button's position information relative to viewport
      const buttonRect = getViewportPosition(buttonEl);
      
      // Bail if we still don't have valid dimensions
      if (buttonRect.width === 0 || buttonRect.height === 0) {
        console.error('Invalid button dimensions');
        return;
      }
      
      // Detect device type with multiple methods for reliability
      const isMobile = window.innerWidth < 640 || 
                     window.navigator.userAgent.match(/Mobi|Android/i) || 
                     (buttonEl.id === 'command-button-mobile') || 
                     window.matchMedia('(max-width: 640px)').matches;
      
      const isPortrait = window.innerHeight > window.innerWidth;
      
      // Estimated menu dimensions
      const menuHeight = commands.length * 60 + 20; // 60px per command + padding
      const menuWidth = 256; // Fixed width of the menu
      
      let top, left;
      
      if (isMobile) {
        // VERY SIMPLE mobile positioning - always top left with fixed position
        top = 80; // Fixed position 80px from top (to avoid headers)
        left = 140; // Fixed position from left
      } else {
        // Desktop positioning - above the button
        top = buttonRect.top - menuHeight - 10; // 10px gap
        left = buttonRect.left + (buttonRect.width / 2);
        
        // Boundary checks for desktop
        top = Math.max(10, top); // Ensure it doesn't go off the top
        
        // Ensure it doesn't go off the sides
        const rightEdge = left + (menuWidth/2);
        const leftEdge = left - (menuWidth/2);
        
        if (rightEdge > window.innerWidth - 10) {
          left = window.innerWidth - 10 - (menuWidth/2); // Too far right
        }
        
        if (leftEdge < 10) {
          left = 10 + (menuWidth/2); // Too far left
        }
      }
      
      const newPosition = {
        top,
        left,
        buttonWidth: buttonRect.width,
        buttonCenterX: buttonRect.width / 2,
        isMobile
      };
      
      setPosition(newPosition);
    }, 50); // 50ms delay

    return () => clearTimeout(timer);
  }, [isOpen, commands.length]);

  // Update position on window resize
  useEffect(() => {
    if (!isOpen) return;
    
    const updatePosition = () => {
      // Try to get the button by ID first, then fall back to ref
      const buttonEl = document.getElementById('command-button') || 
                      document.getElementById('command-button-mobile') || 
                      (buttonRef?.current ? buttonRef.current : null);
      
      if (!buttonEl) {
        console.error('Could not find command button element on resize');
        return;
      }
      
      const buttonRect = getViewportPosition(buttonEl);
      
      // Bail if we still don't have valid dimensions
      if (buttonRect.width === 0 || buttonRect.height === 0) {
        console.error('Invalid button dimensions on resize');
        return;
      }
      
      // Detect device type with multiple methods for reliability
      const isMobile = window.innerWidth < 640 || 
                     window.navigator.userAgent.match(/Mobi|Android/i) || 
                     (buttonEl.id === 'command-button-mobile') || 
                     window.matchMedia('(max-width: 640px)').matches;
      
      const isPortrait = window.innerHeight > window.innerWidth;
      
      // Estimated menu dimensions
      const menuHeight = commands.length * 60 + 20; // 60px per command + padding
      const menuWidth = 256; // Fixed width of the menu
      
      let top, left;
      
      if (isMobile) {
        // VERY SIMPLE mobile positioning - always top left with fixed position
        top = 80; // Fixed position 80px from top (to avoid headers)
        left = 140; // Fixed position from left
      } else {
        // Desktop positioning - above the button
        top = buttonRect.top - menuHeight - 10; // 10px gap
        left = buttonRect.left + (buttonRect.width / 2);
        
        // Boundary checks for desktop
        top = Math.max(10, top); // Ensure it doesn't go off the top
        
        // Ensure it doesn't go off the sides
        const rightEdge = left + (menuWidth/2);
        const leftEdge = left - (menuWidth/2);
        
        if (rightEdge > window.innerWidth - 10) {
          left = window.innerWidth - 10 - (menuWidth/2); // Too far right
        }
        
        if (leftEdge < 10) {
          left = 10 + (menuWidth/2); // Too far left
        }
      }
      
      const newPosition = {
        top,
        left,
        buttonWidth: buttonRect.width,
        buttonCenterX: buttonRect.width / 2,
        isMobile
      };
      
      setPosition(newPosition);
    };
    
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [isOpen, commands.length]);

  // Handle click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        dropupRef.current && 
        !dropupRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    // Delay adding the event listener slightly to prevent immediate closing
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, buttonRef]);

  if (!portalContainer || !isOpen) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Semi-transparent backdrop */}
          <div 
            className="fixed inset-0 bg-black/10" 
            style={{ pointerEvents: 'auto' }}
            onClick={onClose}
          />
          
          {/* Dropup menu */}
          <motion.div
            ref={dropupRef}
            className="fixed bg-[var(--cardBackground)] rounded-lg shadow-lg border border-[var(--border)]"
            style={{
              top: position.top,
              left: position.left,
              width: position.isMobile ? '240px' : '256px', // Smaller width for mobile
              maxWidth: position.isMobile ? '240px' : '256px',
              transform: position.isMobile ? 'none' : 'translateX(-50%)', // Only center on desktop
              zIndex: 9999
            }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-2">
              {commands.map((command) => (
                <div 
                  key={command.command} 
                  className="flex items-center gap-3 p-2 hover:bg-[var(--dropdownHover)] rounded-md cursor-pointer transition-colors"
                  onClick={() => {
                    onInsertCommand(command.command);
                    onClose();
                  }}
                >
                  <div className="flex-shrink-0">
                    {command.icon}
                  </div>
                  <div className="flex-1">
                    <div className="font-mono text-sm font-medium text-[var(--text)]">
                      {command.command}
                    </div>
                    <div className="text-xs text-[var(--textSecondary)]">
                      {command.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Only show arrow on desktop */}
            {!position.isMobile && (
              <div 
                className="absolute h-3 w-3 bottom-[-6px] transform rotate-[225deg] bg-[var(--cardBackground)] border-r border-b border-[var(--border)]"
                style={{
                  left: `${position.buttonCenterX}px`
                }}
              ></div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    portalContainer
  );
};

export default CommandsDropup;